import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDrugsListEnhanced,
  fetchTagsList,
} from "../../../../redux/Thunk/Product/ProductThunks";
import Pagination from "../../../common/Pagination";
import DrugModal from "./DrugModal";
import AssignedProductsDrawer from "./AssignedProductsDrawer";
import DeleteDrugWarningModal from "./DeleteDrugWarningModal";
import MergeDrugsModal from "./MergeDrugsModal";
import ProductTagsSelector from "../ProductTagsSelector";

const Drugs = () => {
  const dispatch = useDispatch();
  const drugManagement = useSelector((state) => state?.product?.drugManagement) || {};
  const drugs = drugManagement?.drugs || [];
  const currentPage = drugManagement?.currentPage || 1;
  const limit = drugManagement?.limit || 10;
  const totalItems = drugManagement?.totalItems || 0;
  const totalPages = drugManagement?.totalPages || 1;
  const stats = drugManagement?.stats || {
    totalDrugs: 0,
    totalAssigned: 0,
    totalUnassigned: 0,
  };
  const loading = drugManagement?.loading ?? false;

  // Local filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("all"); // 'all', 'assigned', 'unassigned'
  const [selectedTagFilters, setSelectedTagFilters] = useState([]);
  const [sortBy, setSortBy] = useState("drugName");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [page, setPage] = useState(1);

  // Modal / Drawer states
  const [isDrugModalOpen, setIsDrugModalOpen] = useState(false);
  const [editingDrug, setEditingDrug] = useState(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerDrug, setDrawerDrug] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingDrug, setDeletingDrug] = useState(null);

  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergingDrug, setMergingDrug] = useState(null);

  // Load tags once
  useEffect(() => {
    dispatch(fetchTagsList());
  }, [dispatch]);

  const tagIdsString = useMemo(() => {
    return selectedTagFilters
      .map((t) => (typeof t === "object" ? t.id : t))
      .filter(Boolean)
      .join(",");
  }, [selectedTagFilters]);

  // Fetch drugs with debounce for search
  const loadDrugs = useCallback(() => {
    dispatch(
      fetchDrugsListEnhanced({
        page,
        limit,
        search: searchTerm,
        filter: assignmentFilter,
        tagIds: tagIdsString,
        sortBy,
        sortOrder,
      })
    );
  }, [dispatch, page, limit, searchTerm, assignmentFilter, tagIdsString, sortBy, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadDrugs();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadDrugs]);

  // Reset page when filters change
  const handleFilterChange = (newFilter) => {
    setAssignmentFilter(newFilter);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleTagsFilterChange = (tags) => {
    setSelectedTagFilters(tags);
    setPage(1);
  };

  const handleSortChange = (e) => {
    const val = e.target.value;
    if (val === "assignedCountDesc") {
      setSortBy("assignedProductCount");
      setSortOrder("DESC");
    } else if (val === "assignedCountAsc") {
      setSortBy("assignedProductCount");
      setSortOrder("ASC");
    } else if (val === "createdAtDesc") {
      setSortBy("createdAt");
      setSortOrder("DESC");
    } else if (val === "nameDesc") {
      setSortBy("drugName");
      setSortOrder("DESC");
    } else {
      setSortBy("drugName");
      setSortOrder("ASC");
    }
    setPage(1);
  };

  // Actions
  const handleOpenCreateModal = () => {
    setEditingDrug(null);
    setIsDrugModalOpen(true);
  };

  const handleOpenEditModal = (drug) => {
    setEditingDrug(drug);
    setIsDrugModalOpen(true);
  };

  const handleOpenDrawer = (drug) => {
    setDrawerDrug(drug);
    setIsDrawerOpen(true);
  };

  const handleOpenDeleteModal = (drug) => {
    setDeletingDrug(drug);
    setIsDeleteModalOpen(true);
  };

  const handleOpenMergeModal = (drug = null) => {
    setMergingDrug(drug);
    setIsMergeModalOpen(true);
  };

  // Export CSV
  const handleExportCSV = () => {
    if (!drugs || drugs.length === 0) return;
    const headers = ["ID", "Drug Name", "Assigned Products Count", "Tags", "Created At"];
    const rows = drugs.map((d) => [
      d.id,
      `"${(d.drugName || "").replace(/"/g, '""')}"`,
      d.assignedProductCount || 0,
      `"${(d.tags?.map((t) => t.name).join(", ") || "").replace(/"/g, '""')}"`,
      d.createdAt ? new Date(d.createdAt).toLocaleDateString() : "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `drugs_inventory_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full space-y-6">
      {/* Top Statistic Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total Master Drugs */}
        <div className="p-4 bg-white rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
              Total Master Drugs
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-gray-900 tracking-tight">
                {stats?.totalDrugs || totalItems || 0}
              </span>
              <span className="text-xs font-semibold text-gray-500">Molecules</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Master drug catalog</p>
          </div>
          <div className="w-11 h-11 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 border border-primary-100">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
              />
            </svg>
          </div>
        </div>

        {/* Assigned Active Drugs */}
        <div
          onClick={() => handleFilterChange("assigned")}
          className={`p-4 rounded-2xl border shadow-xs flex items-center justify-between transition-all cursor-pointer ${
            assignmentFilter === "assigned"
              ? "bg-emerald-50/50 border-emerald-300 ring-2 ring-emerald-400/20"
              : "bg-white border-gray-200/80 hover:border-emerald-200"
          }`}
        >
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">
              Active in Products
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-gray-900 tracking-tight">
                {stats?.totalAssigned || 0}
              </span>
              <span className="text-xs font-semibold text-emerald-700">Assigned</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">Linked to catalog products</p>
          </div>
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        {/* Unassigned / Orphaned Drugs */}
        <div
          onClick={() => handleFilterChange("unassigned")}
          className={`p-4 rounded-2xl border shadow-xs flex items-center justify-between transition-all cursor-pointer ${
            assignmentFilter === "unassigned"
              ? "bg-amber-50/50 border-amber-300 ring-2 ring-amber-400/20"
              : "bg-white border-gray-200/80 hover:border-amber-200"
          }`}
        >
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600">
              Unassigned / Orphaned
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-gray-900 tracking-tight">
                {stats?.totalUnassigned || 0}
              </span>
              <span className="text-xs font-semibold text-amber-700">Unassigned</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">0 linked catalog products</p>
          </div>
          <div className="w-11 h-11 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Main Container Card */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {/* Toolbar Header */}
        <div className="p-4 sm:p-5 border-b border-gray-100 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Left: Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search drug molecule name (e.g. Paracetamol, Metformin)..."
                className="w-full pl-9 pr-4 py-2 bg-gray-50/70 border border-gray-200 rounded-xl text-xs font-medium text-gray-800 placeholder:text-gray-400 focus:bg-white focus:outline-hidden focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 shadow-2xs transition-all"
              />
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3 top-2.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0">
              <button
                type="button"
                onClick={() => handleOpenMergeModal()}
                className="px-3 py-2 bg-white border border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-purple-700 text-xs font-bold rounded-xl shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
                title="Merge duplicate drugs into a single canonical drug"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
                <span>Merge Drugs</span>
              </button>

              <button
                type="button"
                onClick={handleExportCSV}
                className="px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-bold rounded-xl shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
                title="Export current drug list to CSV"
              >
                <svg
                  className="w-3.5 h-3.5 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span>Export CSV</span>
              </button>

              <button
                type="button"
                onClick={handleOpenCreateModal}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>Add Drug</span>
              </button>
            </div>
          </div>

          {/* Filters Bar: Segmented Assignment Tabs + Tag Filter + Sort */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-2">
            {/* Segmented Filter Pills */}
            <div className="bg-gray-100 p-1 rounded-xl flex items-center gap-1 shrink-0 w-fit">
              <button
                type="button"
                onClick={() => handleFilterChange("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${assignmentFilter === "all"
                    ? "bg-white text-gray-900 shadow-2xs"
                    : "text-gray-500 hover:text-gray-800"
                  }`}
              >
                All ({stats?.totalDrugs || 0})
              </button>
              <button
                type="button"
                onClick={() => handleFilterChange("assigned")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${assignmentFilter === "assigned"
                    ? "bg-white text-emerald-800 shadow-2xs font-black"
                    : "text-gray-500 hover:text-gray-800"
                  }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Assigned ({stats?.totalAssigned || 0})
              </button>
              <button
                type="button"
                onClick={() => handleFilterChange("unassigned")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${assignmentFilter === "unassigned"
                    ? "bg-white text-amber-800 shadow-2xs font-black"
                    : "text-gray-500 hover:text-gray-800"
                  }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                Unassigned ({stats?.totalUnassigned || 0})
              </button>
            </div>

            {/* Filter by Tag & Sort */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="min-w-[180px]">
                <ProductTagsSelector
                  selectedTags={selectedTagFilters}
                  onTagsChange={handleTagsFilterChange}
                  label=""
                  placeholder="Filter by Clinical Tag..."
                />
              </div>

              {/* Sort Selector */}
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[11px] font-bold text-gray-400 uppercase">
                  Sort:
                </span>
                <select
                  value={
                    sortBy === "assignedProductCount"
                      ? sortOrder === "DESC"
                        ? "assignedCountDesc"
                        : "assignedCountAsc"
                      : sortBy === "createdAt"
                        ? "createdAtDesc"
                        : sortOrder === "DESC"
                          ? "nameDesc"
                          : "nameAsc"
                  }
                  onChange={handleSortChange}
                  className="px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-hidden focus:border-primary-500 cursor-pointer"
                >
                  <option value="nameAsc">Name (A → Z)</option>
                  <option value="nameDesc">Name (Z → A)</option>
                  <option value="assignedCountDesc">Most Products First</option>
                  <option value="assignedCountAsc">Least Products First</option>
                  <option value="createdAtDesc">Recently Created</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Drug Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-100 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                <th className="py-3 px-4 w-16 text-center">ID</th>
                <th className="py-3 px-4">Drug / Molecule Name</th>
                <th className="py-3 px-4 min-w-[200px]">Clinical & Regulatory Tags</th>
                <th className="py-3 px-4 w-44">Assigned Products</th>
                <th className="py-3 px-4 w-36">Last Updated</th>
                <th className="py-3 px-4 w-28 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-7 w-7 text-primary-600"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      <span className="text-xs text-gray-500 font-medium">
                        Loading drugs catalog...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : drugs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400">
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <span className="font-bold text-gray-800 text-sm">
                        No drugs match your filters
                      </span>
                      <span className="text-xs text-gray-400">
                        Try clearing search terms or selected tags
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                drugs.map((drug) => {
                  const assignedCount = Number(drug.assignedProductCount || 0);
                  return (
                    <tr
                      key={drug.id}
                      className="hover:bg-primary-50/30 transition-colors group"
                    >
                      {/* ID */}
                      <td className="py-3 px-4 text-center font-mono font-bold text-gray-400">
                        #{drug.id}
                      </td>

                      {/* Drug Name */}
                      <td className="py-3 px-4 font-bold text-gray-900">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900 group-hover:text-primary-700 transition-colors">
                            {drug.drugName}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(drug)}
                            title="Edit drug name"
                            className="opacity-0 group-hover:opacity-100 p-1 rounded-md text-gray-400 hover:text-primary-600 hover:bg-gray-100 transition-all cursor-pointer"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>

                      {/* Tags */}
                      <td className="py-3 px-4">
                        {drug.tags && drug.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 items-center">
                            {drug.tags.map((t, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white inline-block shadow-2xs"
                                style={{
                                  backgroundColor: t.color || "#3B82F6",
                                }}
                              >
                                {t.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(drug)}
                            className="text-[10px] font-semibold text-gray-400 hover:text-primary-600 hover:underline inline-flex items-center gap-1 cursor-pointer"
                          >
                            <span>+ Add Tag</span>
                          </button>
                        )}
                      </td>

                      {/* Assigned Products Count */}
                      <td className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => handleOpenDrawer(drug)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all cursor-pointer ${assignedCount > 0
                              ? "bg-primary-50 text-primary-700 border-primary-200 hover:bg-primary-100 hover:border-primary-300 shadow-2xs"
                              : "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200"
                            }`}
                          title="Click to view & manage assigned products"
                        >
                          <svg
                            className="w-3 h-3 shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                          </svg>
                          <span>
                            {assignedCount > 0
                              ? `${assignedCount} Product${assignedCount > 1 ? "s" : ""
                              }`
                              : "0 (Unassigned)"}
                          </span>
                        </button>
                      </td>

                      {/* Last Updated */}
                      <td className="py-3 px-4 text-gray-500 text-[11px] font-medium">
                        {drug.updatedAt
                          ? new Date(drug.updatedAt).toLocaleDateString()
                          : "-"}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            onClick={() => handleOpenDrawer(drug)}
                            title="Manage assigned products"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                              />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(drug)}
                            title="Edit drug name and tags"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenDeleteModal(drug)}
                            title="Delete drug"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        <Pagination
          totalItems={totalItems || 0}
          currentPage={currentPage || 1}
          totalPages={totalPages || 1}
          itemLength={drugs?.length || 0}
          limit={limit || 10}
          onPreviousPage={() => setPage((p) => Math.max(1, p - 1))}
          onNextPage={() => setPage((p) => Math.min(totalPages || 1, p + 1))}
          isPreviousDisabled={(currentPage || 1) <= 1}
          isNextDisabled={(currentPage || 1) >= (totalPages || 1)}
        />
      </div>

      {/* Modals & Slide-overs */}
      <DrugModal
        isOpen={isDrugModalOpen}
        onClose={() => setIsDrugModalOpen(false)}
        drug={editingDrug}
        onSuccess={loadDrugs}
      />

      <AssignedProductsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        drug={drawerDrug}
        onAssignmentChange={loadDrugs}
      />

      <DeleteDrugWarningModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        drug={deletingDrug}
        onSuccess={loadDrugs}
      />

      <MergeDrugsModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        initialDrug={mergingDrug}
        onSuccess={loadDrugs}
      />
    </div>
  );
};

export default Drugs;
